import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Audio } from 'expo-av';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from '../lib/api';
import { Atmosphere, GlassFill, PrimaryButton, GlassPill, CheckCircleIcon, PlayCircleIcon, CalendarIcon, SparkleIcon } from '../olmps/glass';
import { CloudBackdrop, MoonGlow } from '../olmps/primitives';
import type { ToolProps } from '../olmps/ToolHost';
import {
  fetchAutoScheduleLogs,
  keepAllAutoScheduleLogs,
  keepAutoScheduleLog,
  manuallyRescheduleLoggedTask,
  rescheduleTaskToNextSlot,
  type AutoScheduleLogEntry,
  type Group,
} from '../lib/autoSchedule';
import { useOnboarding, useTourTarget, tourBus } from '../v10/onboarding';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { ScreenHelp } from '../v10/ScreenHelp';
import { clioHelp } from '../olmps/help/clioHelp';

// Full-fidelity native CLIO — a faithful port of the web journal
// (src/app/journal/page.tsx), adapted to a phone-width panel.
//
// Reads/writes the SAME `journal_entries` rows the web app uses
// (entry_type_id + answers JSON keyed by prompt id), and reads the SAME
// `user_settings` row (custom_journal_entry_types, telos_settings,
// guiding-principle flags, tkdt_items, monthly/weekly goals, groups) so
// entry types, prompts, guiding principles, and the itinerary's group
// time-block scheduling rules configured on the web show up here
// identically.
//
// Today's Itinerary now includes the SAME scheduling actions as web
// (src/lib/autoSchedule.ts is the single source of truth for the
// slot-finding algorithm used by "Started"/"Reschedule" — see that file's
// header for exactly which web function each entry point ports and any
// deliberate divergences from a buggy web code path):
//   - Done: mark task complete / delete calendar event.
//   - Started: logs a reason to `itinerary_logs`, then reschedules the item
//     exactly like the plain Reschedule action below.
//   - Reschedule: for tasks, finds the item's own group's next open slot
//     starting tomorrow (matches web's "intelligent reschedule"); for
//     calendar events (which have no group), opens a manual date/time
//     picker (matches web's RescheduleDialog).
//   - The auto-schedule verification bell (Keep / Keep All / Reschedule),
//     shown only when the selected entry type has `showAutoVerification`
//     enabled, matching web exactly. A List/Grid toggle mirrors web's
//     Popover — Grid shows a 7-day-wide week grid with pending
//     verification items placed on their day (web reference:
//     `JournalAutoScheduleCalendarView` in journal/page.tsx — mobile omits
//     the intraday time-slot rows/group-block overlays for phone width,
//     keeping the same day columns, item placement, and Keep/Reschedule
//     actions).
//   - TELOS domain hit/miss cards — shown when the selected entry type's
//     `showTelosDomainCards` (or the global `show_telos_domain_cards`
//     setting) is enabled: one card per active TELOS domain with its
//     name/color and "Did the work" / "Missed it" buttons that write a
//     `pending` mark to the SAME `telos_state` row TelosTool.tsx writes
//     (web reference: journal/page.tsx's `markTelosDomain`). The cycle
//     visualization and "Next Cycle" advance button are not duplicated here —
//     TelosTool.tsx remains the place to advance a cycle.
//   - Per-entry-type guiding-principle font customization
//     (`guidingPrincipleFont`: family/size/color/bold/italic/underline) —
//     principles render using the entry type's stored font (falling back
//     to the global `guiding_principle_font` setting, then a default
//     italic slate style), matching web's inline `fontStyle` object.

// ─── Types mirrored from src/interfaces/settings.ts ────────────────────────

interface JournalPrompt {
  id: string;
  text: string;
  type: 'text' | 'dropdown';
  options?: string[];
}

interface WordCountSettings {
  showWordCount?: boolean;
  useWordCountTarget?: boolean;
  wordCountTarget?: number;
}

interface CustomJournalEntryType {
  id: string;
  title: string;
  prompts: JournalPrompt[];
  wordCountSettings?: WordCountSettings;
  showTelosDomainCards?: boolean;
  showGuidingPrinciples?: boolean;
  guidingPrincipleIds?: string[];
  guidingPrincipleFont?: GuidingPrincipleFont;
  showTKDT?: boolean;
  showItinerary?: boolean;
  showGoals?: boolean;
  goalIds?: string[];
  additionalEntryEnabled?: boolean;
  showAutoVerification?: boolean;
}

// Web reference: src/interfaces/settings.ts GuidingPrincipleFont / DEFAULT_GUIDING_PRINCIPLE_FONT.
interface GuidingPrincipleFont {
  fontFamily: string;
  fontSize: number;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

const DEFAULT_GUIDING_PRINCIPLE_FONT: GuidingPrincipleFont = {
  fontFamily: 'serif',
  fontSize: 14,
  color: '',
  bold: false,
  italic: true,
  underline: false,
};

interface MonthlyGoal {
  id: string;
  monthKey: string;
  text: string;
  completed: boolean;
  sortOrder: number;
}

interface WeeklyGoal {
  id: string;
  weekKey: string;
  text: string;
  completed: boolean;
  sortOrder: number;
}

interface TelosDomainConfig {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
  cascade?: { customPrinciples?: string[] };
}

// Web reference: src/interfaces/settings.ts TelosDomainState / journal/page.tsx
// `telosDomainStates` + `markTelosDomain`.
type TelosPending = 'hit' | 'miss' | null;

interface TelosDomainState {
  actionOffset: number;
  systemOffset: number;
  streak: number;
  missStreak: number;
  pending: TelosPending;
}

function createDefaultTelosDomainState(): TelosDomainState {
  return { actionOffset: 0, systemOffset: 0, streak: 0, missStreak: 0, pending: null };
}

interface AdditionalJournalEntry {
  id: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

interface TKDTItem {
  id: string;
  label: string;
  count: number;
}

// ─── EPIC types (matched with EpicTool.tsx) ────────────────────────────────

type EpicSkinId =
  | 'chronicle'
  | 'parallel_broadcast'
  | 'intel_briefing'
  | 'dojo_report'
  | 'ships_log'
  | 'mythic_realm'
  | 'cinematic_noir'
  | 'resonance_sweep'
  | 'tribunal'
  | 'rotating_anchor';

interface EpicEpisode {
  id: string;
  userId: string;
  date: string;
  episodeNumber: number;
  title: string;
  skin: EpicSkinId;
  script: string;
  editedScript?: string | null;
  unresolvedQuestion: string;
  audioPath?: string | null;
  audioStatus: 'none' | 'pending' | 'ready' | 'error' | 'unconfigured';
  status: 'generating' | 'ready' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface EpicGenerateResponse {
  episode: EpicEpisode | null;
  archive: any[];
}

interface CalendarEventRow {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  repeat?: string | null;
  end_date?: string | null;
}

interface TaskRow {
  id: string;
  title: string;
  done: boolean;
  scheduled: string | null;
  etc: string | null;
}

interface ItineraryItem {
  id: string; // 'task-<id>' or the calendar_events id
  rawId: string; // underlying db row id, unprefixed
  title: string;
  startTime: string;
  isTask: boolean;
}

// ─── Guiding-principle presets, condensed from src/app/telos/cascade-presets.ts
// (principles only — visions/habits/tasks aren't needed on the Journal page). ──

const PRESET_PRINCIPLES: Record<string, { id: string; text: string }[]> = {
  'telos-body': [
    { id: 'body-p1', text: 'Energy is earned through discipline, not motivation.' },
    { id: 'body-p2', text: 'The body adapts to what you consistently demand of it.' },
    { id: 'body-p3', text: 'Neglect compounds faster than effort does.' },
    { id: 'body-p4', text: 'Comfort is the enemy of capability.' },
    { id: 'body-p5', text: 'Rest is a strategy, not a reward.' },
  ],
  'telos-self': [
    { id: 'self-p1', text: 'Identity is built by what you repeatedly do, not what you believe about yourself.' },
    { id: 'self-p2', text: 'Growth requires honest discomfort, not positive thinking.' },
    { id: 'self-p3', text: 'What you avoid defines you more than what you pursue.' },
    { id: 'self-p4', text: "An unexamined pattern will repeat until it's named." },
    { id: 'self-p5', text: "You can only manage what you're willing to see clearly." },
  ],
  'telos-resources': [
    { id: 'res-p1', text: 'Every resource is either growing or shrinking — nothing holds.' },
    { id: 'res-p2', text: "What you don't track, you lose." },
    { id: 'res-p3', text: 'Systems produce wealth; effort alone produces income.' },
    { id: 'res-p4', text: 'Ownership without maintenance is just delayed loss.' },
    { id: 'res-p5', text: 'The goal is options, not things.' },
  ],
  'telos-relationships': [
    { id: 'rel-p1', text: 'Every relationship is an exchange — the question is whether both sides know the terms.' },
    { id: 'rel-p2', text: 'Proximity is not loyalty.' },
    { id: 'rel-p3', text: 'You teach people how to treat you by what you tolerate.' },
    { id: 'rel-p4', text: 'Reputation is built in private and revealed in public.' },
    { id: 'rel-p5', text: 'The strongest bonds survive honest conflict.' },
  ],
};

function findPresetPrinciples(domain: { id: string; name: string }): { id: string; text: string }[] {
  if (PRESET_PRINCIPLES[domain.id]) return PRESET_PRINCIPLES[domain.id];
  const byName = `telos-${domain.name.toLowerCase()}`;
  return PRESET_PRINCIPLES[byName] ?? [];
}

function computeGuidingPrinciples(selectedIds: string[], domains: TelosDomainConfig[]): string[] {
  const out: string[] = [];
  domains
    .filter((d) => d.isActive)
    .forEach((domain) => {
      findPresetPrinciples(domain).forEach((p) => {
        if (selectedIds.includes(p.id)) out.push(p.text);
      });
      (domain.cascade?.customPrinciples ?? []).forEach((text, idx) => {
        if (selectedIds.includes(`${domain.id}-custom-p-${idx}`)) out.push(text);
      });
    });
  return out;
}

// ─── Date helpers (no date-fns in this app — plain Date arithmetic) ────────

const MONTH_NAMES_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYMD(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function atMidnight(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

function formatLongDate(d: Date): string {
  return `${MONTH_NAMES_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function hhmm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function addMinutes(d: Date, mins: number): Date {
  return new Date(d.getTime() + mins * 60000);
}

// Parses a task's `scheduled` field: "yyyy-MM-dd HH:mm" (or "...THH:mm"),
// falling back to a date-only "yyyy-MM-dd" (treated as midnight).
function parseScheduled(scheduled: string): Date | null {
  const full = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(scheduled);
  if (full) {
    return new Date(
      Number(full[1]), Number(full[2]) - 1, Number(full[3]), Number(full[4]), Number(full[5])
    );
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(scheduled);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 0, 0);
  }
  return null;
}

// Web reference: src/app/calendar/page.tsx parseETCToMinutes.
function parseETCToMinutes(etcLabel: string | null | undefined): number | null {
  if (!etcLabel) return null;
  const cleaned = etcLabel.toLowerCase().trim();
  const numMatch = cleaned.match(/^(\d+(\.\d+)?)/);
  if (!numMatch) return null;
  const num = parseFloat(numMatch[1]);
  if (Number.isNaN(num)) return null;
  if (cleaned.includes('min')) return num;
  if (cleaned.includes('hour')) return num * 60;
  if (cleaned.includes('day')) return num * 8 * 60;
  if (num <= 180) return num;
  return null;
}

// Web reference: isEventOccurringOnDate in src/app/journal/page.tsx.
function occursOnDate(
  item: { date: string; isTask: boolean; repeat?: string | null; endDate?: string | null },
  checkDate: Date
): boolean {
  const start = parseYMD(item.date);
  if (!start) return false;
  const check = atMidnight(checkDate);
  if (item.isTask) {
    return check.getTime() === atMidnight(start).getTime();
  }
  const startMid = atMidnight(start);
  if (check.getTime() < startMid.getTime()) return false;
  // A repeating series stops after its end_date (expired timetables used to
  // keep showing forever).
  const end = item.endDate ? parseYMD(item.endDate) : null;
  if (end && check.getTime() > atMidnight(end).getTime()) return false;
  const repeat = item.repeat || 'none';
  if (repeat === 'none') return check.getTime() === startMid.getTime();
  if (repeat === 'daily') return true;
  if (repeat === 'weekly') return check.getDay() === startMid.getDay();
  if (repeat === 'monthly') return check.getDate() === startMid.getDate();
  if (repeat === 'yearly') {
    return check.getMonth() === startMid.getMonth() && check.getDate() === startMid.getDate();
  }
  return false;
}

function buildItinerary(events: CalendarEventRow[], tasks: TaskRow[], selectedDate: Date): ItineraryItem[] {
  const items: ItineraryItem[] = [];

  events.forEach((e) => {
    if (!e.title || !e.date || !e.start_time || !e.end_time) return;
    if (occursOnDate({ date: e.date, isTask: false, repeat: e.repeat, endDate: e.end_date }, selectedDate)) {
      items.push({ id: e.id, rawId: e.id, title: e.title, startTime: e.start_time, isTask: false });
    }
  });

  tasks.forEach((t) => {
    if (t.done || !t.title || !t.scheduled) return;
    const scheduledAt = parseScheduled(t.scheduled);
    if (!scheduledAt) return;
    if (occursOnDate({ date: ymd(scheduledAt), isTask: true }, selectedDate)) {
      items.push({ id: `task-${t.id}`, rawId: t.id, title: t.title, startTime: hhmm(scheduledAt), isTask: true });
    }
  });

  items.sort((a, b) => a.startTime.localeCompare(b.startTime));
  return items;
}

// ─── Misc helpers ───────────────────────────────────────────────────────────

function countWords(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

function genId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Auto-schedule verification bell — List view only, matching web's
// journal/page.tsx Popover (Keep / Keep All / Reschedule). ────────────────

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Web reference: journal/page.tsx's inline `parse(entry.scheduled_to, 'yyyy-MM-dd HH:mm', ...)`
// + `format(p, 'EEE, MMM d · h:mm a')` in the verification list row.
function formatLogDate(scheduledTo: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(scheduledTo);
  if (!m) return scheduledTo;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  const label = `${MONTH_NAMES_SHORT[date.getMonth()]} ${date.getDate()}`;
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${label}, ${hour}:${pad2(minute)} ${suffix}`;
}

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Web reference: journal/page.tsx's `JournalAutoScheduleCalendarView` — a
// week grid with 7 day columns. Mobile keeps the same day-column layout and
// pending-item placement/actions but drops the intraday time-slot rows and
// group-block overlays (out of scope on a phone-width panel — see header
// comment for the full divergence note).
function VerificationGridView({
  logs,
  weekStart,
  onPrevWeek,
  onNextWeek,
  onToday,
  onKeep,
  onReschedule,
}: {
  logs: AutoScheduleLogEntry[];
  weekStart: Date;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onKeep: (log: AutoScheduleLogEntry) => void;
  onReschedule: (log: AutoScheduleLogEntry) => void;
}) {
  const styles = useStyles();
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayYmd = ymd(atMidnight(new Date()));

  const logsByDay = useMemo(() => {
    const map = new Map<string, AutoScheduleLogEntry[]>();
    logs.forEach((log) => {
      const dayKey = log.scheduled_to.slice(0, 10);
      const list = map.get(dayKey) ?? [];
      list.push(log);
      map.set(dayKey, list);
    });
    return map;
  }, [logs]);

  return (
    <View style={styles.gridRoot}>
      <View style={styles.gridNavRow}>
        <Pressable style={styles.gridNavBtn} onPress={onPrevWeek}>
          <Text style={styles.gridNavBtnText}>‹</Text>
        </Pressable>
        <Pressable style={styles.gridTodayBtn} onPress={onToday}>
          <Text style={styles.gridTodayBtnText}>Today</Text>
        </Pressable>
        <Pressable style={styles.gridNavBtn} onPress={onNextWeek}>
          <Text style={styles.gridNavBtnText}>›</Text>
        </Pressable>
        <Text style={styles.gridWeekLabel}>
          {formatLongDate(days[0]).replace(`, ${days[0].getFullYear()}`, '')} –{' '}
          {formatLongDate(days[6]).replace(`, ${days[6].getFullYear()}`, '')}
        </Text>
      </View>
      <View style={styles.gridColumns}>
        {days.map((day, i) => {
          const dayKey = ymd(day);
          const isToday = dayKey === todayYmd;
          const dayLogs = logsByDay.get(dayKey) ?? [];
          return (
            <View key={dayKey} style={[styles.gridDayCol, isToday && styles.gridDayColToday]}>
              <Text style={[styles.gridDayHeader, isToday && styles.gridDayHeaderToday]}>
                {WEEKDAY_LETTERS[i]} {day.getDate()}
              </Text>
              <ScrollView style={styles.gridDayItems} contentContainerStyle={styles.gridDayItemsContent}>
                {dayLogs.length === 0 ? (
                  <Text style={styles.gridDayEmpty}>—</Text>
                ) : (
                  dayLogs.map((log) => (
                    <Pressable key={log.id} style={styles.gridItem} onPress={() => onReschedule(log)}>
                      <Text style={styles.gridItemText} numberOfLines={2}>
                        {log.task_title}
                      </Text>
                      <Pressable
                        style={styles.gridItemKeepBtn}
                        onPress={(e) => {
                          e.stopPropagation();
                          onKeep(log);
                        }}
                      >
                        <Text style={styles.gridItemKeepBtnText}>Keep</Text>
                      </Pressable>
                    </Pressable>
                  ))
                )}
              </ScrollView>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// Web reference: journal/page.tsx's Popover list/calendar toggle
// (lines ~1130-1182). List = simple row list; Grid = week-calendar view.
function VerificationModal({
  visible,
  logs,
  view,
  onChangeView,
  onClose,
  onKeep,
  onKeepAll,
  onReschedule,
}: {
  visible: boolean;
  logs: AutoScheduleLogEntry[];
  view: 'list' | 'grid';
  onChangeView: (view: 'list' | 'grid') => void;
  onClose: () => void;
  onKeep: (log: AutoScheduleLogEntry) => void;
  onKeepAll: () => void;
  onReschedule: (log: AutoScheduleLogEntry) => void;
}) {
  const styles = useStyles();
  const [weekStart, setWeekStart] = useState(() => {
    const now = atMidnight(new Date());
    return addDays(now, -now.getDay());
  });

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
<GlassFill radius={radii.panel} />
          <View style={styles.sheetTitleRow}>
            <Text style={styles.sheetTitle}>Auto-Scheduled Verification</Text>
            <View style={styles.viewToggleRow}>
              <Pressable
                style={[styles.viewToggleBtn, view === 'list' && styles.viewToggleBtnActive]}
                onPress={() => onChangeView('list')}
              >
                <Text style={[styles.viewToggleText, view === 'list' && styles.viewToggleTextActive]}>List</Text>
              </Pressable>
              <Pressable
                style={[styles.viewToggleBtn, view === 'grid' && styles.viewToggleBtnActive]}
                onPress={() => onChangeView('grid')}
              >
                <Text style={[styles.viewToggleText, view === 'grid' && styles.viewToggleTextActive]}>Grid</Text>
              </Pressable>
            </View>
          </View>

          {logs.length === 0 ? (
            <Text style={[styles.emptyInline, styles.sheetEmptyPadded]}>No pending verifications.</Text>
          ) : view === 'list' ? (
            <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
              <Pressable style={styles.keepAllBtn} onPress={onKeepAll}>
                <Text style={styles.keepAllBtnText}>Keep All ({logs.length})</Text>
              </Pressable>
              {logs.map((log) => (
                <View key={log.id} style={styles.logRow}>
                  <View style={styles.logRowText}>
                    <Text style={styles.logTitle} numberOfLines={1}>
                      {log.task_title}
                    </Text>
                    <Text style={styles.logMeta}>
                      {formatLogDate(log.scheduled_to)} · {log.action === 'auto_rescheduled' ? 'rescheduled' : 'scheduled'}
                    </Text>
                  </View>
                  <View style={styles.logRowActions}>
                    <Pressable style={styles.logActionBtn} onPress={() => onKeep(log)}>
                      <Text style={styles.logActionText}>Keep</Text>
                    </Pressable>
                    <Pressable style={styles.logActionBtn} onPress={() => onReschedule(log)}>
                      <Text style={styles.logActionText}>Reschedule</Text>
                    </Pressable>
                  </View>
                </View>
              ))}
            </ScrollView>
          ) : (
            <VerificationGridView
              logs={logs}
              weekStart={weekStart}
              onPrevWeek={() => setWeekStart((prev) => addDays(prev, -7))}
              onNextWeek={() => setWeekStart((prev) => addDays(prev, 7))}
              onToday={() => {
                const now = atMidnight(new Date());
                setWeekStart(addDays(now, -now.getDay()));
              }}
              onKeep={onKeep}
              onReschedule={onReschedule}
            />
          )}

          <View style={styles.sheetFooter}>
            <Pressable style={styles.sheetDoneButton} onPress={onClose}>
              <Text style={styles.sheetDoneButtonText}>Done</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// Manual date/time entry for a logged entry's Reschedule action — web
// reference: journal/page.tsx's <RescheduleDialog> wired to `rescheduleLogItem`
// / `handleLogRescheduleComplete` (a MANUAL override, not the auto slot-finder).
function LogRescheduleModal({
  log,
  onCancel,
  onSubmit,
}: {
  log: AutoScheduleLogEntry | null;
  onCancel: () => void;
  onSubmit: (dateStr: string, timeStr: string) => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [dateVal, setDateVal] = useState('');
  const [timeVal, setTimeVal] = useState('');

  useEffect(() => {
    if (log) {
      const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(log.scheduled_to);
      setDateVal(m ? m[1] : ymd(atMidnight(new Date())));
      setTimeVal(m ? m[2] : '09:00');
    }
  }, [log]);

  return (
    <Modal visible={!!log} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <View style={styles.modalBox}>
<GlassFill radius={radii.panel} />
          <Text style={styles.modalTitle}>Reschedule "{log?.task_title}"</Text>
          <Text style={styles.fieldLabel}>Date (yyyy-MM-dd)</Text>
          <TextInput
            style={styles.modalInput}
            value={dateVal}
            onChangeText={setDateVal}
            placeholder="yyyy-MM-dd"
            placeholderTextColor={c.slate}
          />
          <View style={styles.chipRow}>
            <Pressable style={styles.modalChip} onPress={() => setDateVal(ymd(atMidnight(new Date())))}>
              <Text style={styles.modalChipText}>Today</Text>
            </Pressable>
            <Pressable style={styles.modalChip} onPress={() => setDateVal(ymd(addDays(atMidnight(new Date()), 1)))}>
              <Text style={styles.modalChipText}>Tomorrow</Text>
            </Pressable>
          </View>
          <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>Time (HH:mm)</Text>
          <TextInput
            style={styles.modalInput}
            value={timeVal}
            onChangeText={setTimeVal}
            placeholder="09:00"
            placeholderTextColor={c.slate}
          />
          <View style={styles.modalButtons}>
            <Pressable style={styles.modalBtn} onPress={onCancel}>
              <Text style={styles.modalBtnText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[styles.modalBtn, styles.modalBtnPrimary]}
              onPress={() => onSubmit(dateVal.trim(), timeVal.trim())}
            >
              <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Save</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// Manual date/time entry for a calendar-event itinerary row's Reschedule
// action — web reference: journal/page.tsx's `handleItineraryItemReschedule`
// branches to `setRescheduleItem(item)` (opening <RescheduleDialog>) whenever
// `!item.isTask`, since calendar events have no group to auto-schedule
// against. This mirrors LogRescheduleModal's simple date+time text-input
// pattern rather than web's full week-grid picker (out of scope on a phone).
function ItineraryRescheduleModal({
  item,
  defaultDate,
  onCancel,
  onSubmit,
}: {
  item: ItineraryItem | null;
  defaultDate: Date;
  onCancel: () => void;
  onSubmit: (dateStr: string, timeStr: string) => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [dateVal, setDateVal] = useState('');
  const [timeVal, setTimeVal] = useState('');

  useEffect(() => {
    if (item) {
      setDateVal(ymd(defaultDate));
      setTimeVal(item.startTime);
    }
  }, [item, defaultDate]);

  return (
    <Modal visible={!!item} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <View style={styles.modalBox}>
<GlassFill radius={radii.panel} />
          <Text style={styles.modalTitle}>Reschedule "{item?.title}"</Text>
          <Text style={styles.fieldLabel}>Date (yyyy-MM-dd)</Text>
          <TextInput
            style={styles.modalInput}
            value={dateVal}
            onChangeText={setDateVal}
            placeholder="yyyy-MM-dd"
            placeholderTextColor={c.slate}
          />
          <View style={styles.chipRow}>
            <Pressable style={styles.modalChip} onPress={() => setDateVal(ymd(atMidnight(new Date())))}>
              <Text style={styles.modalChipText}>Today</Text>
            </Pressable>
            <Pressable style={styles.modalChip} onPress={() => setDateVal(ymd(addDays(atMidnight(new Date()), 1)))}>
              <Text style={styles.modalChipText}>Tomorrow</Text>
            </Pressable>
          </View>
          <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>Time (HH:mm)</Text>
          <TextInput
            style={styles.modalInput}
            value={timeVal}
            onChangeText={setTimeVal}
            placeholder="09:00"
            placeholderTextColor={c.slate}
          />
          <View style={styles.modalButtons}>
            <Pressable style={styles.modalBtn} onPress={onCancel}>
              <Text style={styles.modalBtnText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[styles.modalBtn, styles.modalBtnPrimary]}
              onPress={() => onSubmit(dateVal.trim(), timeVal.trim())}
            >
              <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Save</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// "Started" reason prompt — web reference: journal/page.tsx's "Started Reason
// Dialog" (a <Dialog>/<Textarea>, not window.prompt()) wired to
// `handleItineraryItemStarted` / `saveStartedReason`.
function StartedReasonModal({
  item,
  reason,
  onChangeReason,
  onCancel,
  onSubmit,
}: {
  item: ItineraryItem | null;
  reason: string;
  onChangeReason: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <Modal visible={!!item} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <View style={styles.modalBox}>
<GlassFill radius={radii.panel} />
          <Text style={styles.modalTitle}>Reason for "{item?.title}"</Text>
          <Text style={styles.fieldLabel}>Reason / notes</Text>
          <TextInput
            style={[styles.modalInput, styles.startedReasonInput]}
            value={reason}
            onChangeText={onChangeReason}
            placeholder="Enter reason here…"
            placeholderTextColor={c.slate}
            multiline
            textAlignVertical="top"
          />
          <View style={styles.modalButtons}>
            <Pressable style={styles.modalBtn} onPress={onCancel}>
              <Text style={styles.modalBtnText}>Cancel</Text>
            </Pressable>
            <Pressable style={[styles.modalBtn, styles.modalBtnPrimary]} onPress={onSubmit}>
              <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Log Reason &amp; Reschedule</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/** Tour target wrapper (Pressables take no tour ref). */
function TourBox({ id, children }: { id: string; children: React.ReactNode }) {
  const tt = useTourTarget(id);
  return <View ref={tt.onLayoutRef} collapsable={false}>{children}</View>;
}

// Day outcome review: Complete / Partial / Incomplete + optional note. Stored
// in the day's journal_entries row under a reserved entry_type_id, inside the
// existing `answers` JSON (keys outcome / outcome_note) -- no new column.
// A proper home would be journal_entries.outcome text + outcome_note text.
const OUTCOME_ENTRY_TYPE = 'clio-outcome';
type DayOutcome = 'complete' | 'partial' | 'incomplete';
const OUTCOME_OPTIONS: Array<{ key: DayOutcome; label: string }> = [
  { key: 'complete', label: 'Complete' },
  { key: 'partial', label: 'Partial' },
  { key: 'incomplete', label: 'Incomplete' },
];

// ─── Component ──────────────────────────────────────────────────────────────

export function ClioTool(_props: ToolProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const ob = useOnboarding();

  const [selectedDate, setSelectedDate] = useState<Date>(() => atMidnight(new Date()));

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [entryTypes, setEntryTypes] = useState<CustomJournalEntryType[]>([]);
  const [telosDomains, setTelosDomains] = useState<TelosDomainConfig[]>([]);
  const [globalShowGuidingPrinciples, setGlobalShowGuidingPrinciples] = useState(false);
  const [globalGuidingPrincipleIds, setGlobalGuidingPrincipleIds] = useState<string[]>([]);
  const [globalGuidingPrincipleFont, setGlobalGuidingPrincipleFont] = useState<GuidingPrincipleFont | null>(null);
  const [globalShowTKDT, setGlobalShowTKDT] = useState(false);
  const [globalShowTelosDomainCards, setGlobalShowTelosDomainCards] = useState(false);
  const [telosCycle, setTelosCycle] = useState(0);
  const [telosDomainStates, setTelosDomainStates] = useState<Record<string, TelosDomainState>>({});
  const [tkdtItems, setTkdtItems] = useState<TKDTItem[]>([]);
  const [tkdtOpen, setTkdtOpen] = useState(false);
  const [tkdtNewLabel, setTkdtNewLabel] = useState('');
  const [monthlyGoals, setMonthlyGoals] = useState<MonthlyGoal[]>([]);
  const [weeklyGoals, setWeeklyGoals] = useState<WeeklyGoal[]>([]);

  const [selectedEntryTypeId, setSelectedEntryTypeIdState] = useState<string | null>(null);

  const [entryLoading, setEntryLoading] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [wordCounts, setWordCounts] = useState<Record<string, number>>({});
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null);
  const [currentEntryUpdatedAt, setCurrentEntryUpdatedAt] = useState<string | null>(null);
  const [additionalEntries, setAdditionalEntries] = useState<AdditionalJournalEntry[]>([]);
  // Whether this entry is included in CLIO's EPIC episode content ("EP" toggle).
  const [excludeFromEpic, setExcludeFromEpic] = useState(false);
  const [draftAdditional, setDraftAdditional] = useState<Array<{ id: string; text: string }>>([]);
  const [savingAdditionalId, setSavingAdditionalId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [eventsRaw, setEventsRaw] = useState<CalendarEventRow[]>([]);
  const [tasksRaw, setTasksRaw] = useState<TaskRow[]>([]);
  const [itineraryLoading, setItineraryLoading] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [itineraryBusyId, setItineraryBusyId] = useState<string | null>(null);
  const [startedItem, setStartedItem] = useState<ItineraryItem | null>(null);
  const [startedReason, setStartedReason] = useState('');
  const [eventRescheduleItem, setEventRescheduleItem] = useState<ItineraryItem | null>(null);

  // Auto-schedule verification bell — List and Grid (week-calendar) views,
  // matching web's Popover List/Calendar toggle.
  const [autoScheduleLogs, setAutoScheduleLogs] = useState<AutoScheduleLogEntry[]>([]);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationView, setVerificationView] = useState<'list' | 'grid'>('list');
  const [rescheduleLogItem, setRescheduleLogItem] = useState<AutoScheduleLogEntry | null>(null);

  // EPIC episode state (loads on date change, visible directly in Clio)
  const [epicEpisode, setEpicEpisode] = useState<EpicEpisode | null>(null);
  const [epicLoading, setEpicLoading] = useState(false);
  const [epicError, setEpicError] = useState<string | null>(null);
  const [epicAudioGenerating, setEpicAudioGenerating] = useState(false);
  const [epicAudioPlaying, setEpicAudioPlaying] = useState(false);
  const epicSoundRef = useRef<Audio.Sound | null>(null);

  const isDirtyRef = useRef(false);

  // ── Settings: entry types, guiding-principle sources, TKDT, goals ────────
  const loadSettings = useCallback(async () => {
    if (!userId) return;
    setSettingsLoading(true);
    const { data, error: err } = await supabase
      .from('user_settings')
      .select(
        'custom_journal_entry_types, telos_settings, show_guiding_principles, guiding_principle_ids, guiding_principle_font, show_tkdt, tkdt_items, show_telos_domain_cards, monthly_goals, weekly_goals, journal_selected_entry_type_id, groups'
      )
      .eq('user_id', userId)
      .maybeSingle();

    if (err) {
      setError(err.message);
      setSettingsLoading(false);
      return;
    }

    const rawTypes: any[] = Array.isArray(data?.custom_journal_entry_types) ? data.custom_journal_entry_types : [];
    const types: CustomJournalEntryType[] = rawTypes.map((t) => ({
      ...t,
      prompts: Array.isArray(t.prompts)
        ? t.prompts.map((p: any) => ({ ...p, options: Array.isArray(p.options) ? p.options : [] }))
        : [],
    }));

    setEntryTypes(types);
    setTelosDomains(Array.isArray(data?.telos_settings?.domains) ? data.telos_settings.domains : []);
    setGlobalShowGuidingPrinciples(!!data?.show_guiding_principles);
    setGlobalGuidingPrincipleIds(Array.isArray(data?.guiding_principle_ids) ? data.guiding_principle_ids : []);
    setGlobalGuidingPrincipleFont(data?.guiding_principle_font ?? null);
    setGlobalShowTKDT(!!data?.show_tkdt);
    setGlobalShowTelosDomainCards(!!data?.show_telos_domain_cards);
    setTkdtItems(Array.isArray(data?.tkdt_items) ? data.tkdt_items : []);
    setMonthlyGoals(Array.isArray(data?.monthly_goals) ? data.monthly_goals : []);
    setWeeklyGoals(Array.isArray(data?.weekly_goals) ? data.weekly_goals : []);
    setGroups(Array.isArray(data?.groups) ? data.groups : []);

    const persistedId: string | null = data?.journal_selected_entry_type_id ?? null;
    setSelectedEntryTypeIdState((prev) => {
      if (prev && types.some((t) => t.id === prev)) return prev;
      if (persistedId && types.some((t) => t.id === persistedId)) return persistedId;
      return types[0]?.id ?? null;
    });

    setError(null);
    setSettingsLoading(false);
  }, [userId]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const selectEntryType = useCallback(
    (id: string) => {
      setSelectedEntryTypeIdState(id);
      if (userId) {
        void supabase.from('user_settings').update({ journal_selected_entry_type_id: id }).eq('user_id', userId);
      }
    },
    [userId]
  );

  // ── Itinerary source data (calendar_events + scheduled tasks) ────────────
  const loadItinerary = useCallback(async () => {
    if (!userId) return;
    setItineraryLoading(true);
    const [eventsRes, tasksRes] = await Promise.all([
      supabase.from('calendar_events').select('id, title, date, start_time, end_time, repeat, end_date').eq('user_id', userId),
      supabase
        .from('tasks')
        .select('id, title, done, scheduled, etc')
        .eq('user_id', userId)
        .not('scheduled', 'is', null),
    ]);
    if (eventsRes.error || tasksRes.error) {
      setError((eventsRes.error ?? tasksRes.error)!.message);
    } else {
      setEventsRaw((eventsRes.data ?? []) as CalendarEventRow[]);
      setTasksRaw((tasksRes.data ?? []) as TaskRow[]);
    }
    setItineraryLoading(false);
  }, [userId]);

  useEffect(() => {
    void loadItinerary();
  }, [loadItinerary]);

  const itineraryItems = useMemo(
    () => buildItinerary(eventsRaw, tasksRaw, selectedDate),
    [eventsRaw, tasksRaw, selectedDate]
  );

  // ── Load EPIC episode for the selected date ─────────────────────────────
  useEffect(() => {
    if (!userId) {
      setEpicEpisode(null);
      return;
    }

    let cancelled = false;

    const loadEpicEpisode = async () => {
      setEpicLoading(true);
      setEpicError(null);
      try {
        const dateStr = ymd(selectedDate);
        const data = await apiFetch<EpicGenerateResponse>(
          `/api/epic/generate?date=${dateStr}`
        );
        if (cancelled) return;
        setEpicEpisode(data.episode || null);
      } catch (e: any) {
        if (!cancelled) {
          setEpicError(e?.message ?? 'Failed to load episode');
        }
      } finally {
        if (!cancelled) setEpicLoading(false);
      }
    };

    loadEpicEpisode();
    return () => {
      cancelled = true;
    };
  }, [userId, selectedDate]);

  // Cleanup epic sound on unmount
  useEffect(() => {
    return () => {
      if (epicSoundRef.current) {
        epicSoundRef.current.unloadAsync().catch(() => {});
      }
    };
  }, []);

  // ── EPIC handlers ────────────────────────────────────────────────────────

  const epicPlayAudio = useCallback(async () => {
    if (!epicEpisode?.audioPath) return;

    try {
      if (epicSoundRef.current) {
        const status = await epicSoundRef.current.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await epicSoundRef.current.pauseAsync();
          setEpicAudioPlaying(false);
          return;
        }
      }

      setEpicAudioPlaying(true);
      const sound = new Audio.Sound();
      epicSoundRef.current = sound;

      await sound.loadAsync({ uri: epicEpisode.audioPath });
      await sound.playAsync();

      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) {
          setEpicAudioPlaying(false);
        }
      });
    } catch (e: any) {
      setEpicError(e?.message ?? 'Failed to play audio');
      setEpicAudioPlaying(false);
    }
  }, [epicEpisode?.audioPath]);

  const epicGenerateAudio = useCallback(async () => {
    if (!epicEpisode) return;

    try {
      setEpicAudioGenerating(true);
      setEpicError(null);

      const data = await apiFetch<{ url: string }>(
        '/api/epic/audio',
        {
          method: 'POST',
          body: JSON.stringify({ episodeId: epicEpisode.id }),
        }
      );

      if (data.url) {
        setEpicEpisode((ep) =>
          ep ? { ...ep, audioPath: data.url, audioStatus: 'ready' } : null
        );
      }
    } catch (e: any) {
      setEpicError(e?.message ?? 'Failed to generate audio');
    } finally {
      setEpicAudioGenerating(false);
    }
  }, [epicEpisode]);

  const epicGenerateEpisode = useCallback(async () => {
    if (!userId) return;

    try {
      setEpicLoading(true);
      setEpicError(null);

      const dateStr = ymd(selectedDate);
      const data = await apiFetch<EpicGenerateResponse>(
        '/api/epic/generate',
        {
          method: 'POST',
          body: JSON.stringify({ date: dateStr, force: false }),
        }
      );

      setEpicEpisode(data.episode || null);
    } catch (e: any) {
      setEpicError(e?.message ?? 'Failed to generate episode');
    } finally {
      setEpicLoading(false);
    }
  }, [userId, selectedDate]);

  // ── Day outcome review (Complete / Partial / Incomplete + optional note) ──
  const [outcome, setOutcome] = useState<DayOutcome | null>(null);
  const [outcomeNote, setOutcomeNote] = useState('');
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const [outcomeSaved, setOutcomeSaved] = useState(false);
  const outcomeDateStr = ymd(selectedDate);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setOutcome(null);
    setOutcomeNote('');
    setOutcomeSaved(false);
    void (async () => {
      const { data } = await supabase
        .from('journal_entries')
        .select('answers')
        .eq('user_id', userId)
        .eq('date', outcomeDateStr)
        .eq('entry_type_id', OUTCOME_ENTRY_TYPE)
        .limit(1)
        .maybeSingle();
      if (cancelled || !data) return;
      const a: any = data.answers ?? {};
      if (a.outcome === 'complete' || a.outcome === 'partial' || a.outcome === 'incomplete') {
        setOutcome(a.outcome);
        setOutcomeSaved(true);
      }
      if (typeof a.outcome_note === 'string') setOutcomeNote(a.outcome_note);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, outcomeDateStr]);

  const saveOutcome = async () => {
    if (!userId || !outcome || outcomeSaving) return;
    setOutcomeSaving(true);
    setError(null);
    const now = new Date().toISOString();
    const answersJson = { outcome, outcome_note: outcomeNote.trim() };
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('journal_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('date', outcomeDateStr)
        .eq('entry_type_id', OUTCOME_ENTRY_TYPE)
        .limit(1)
        .maybeSingle();
      if (fetchErr) throw fetchErr;
      if (existing?.id) {
        const { error: upErr } = await supabase
          .from('journal_entries')
          .update({ answers: answersJson, updated_at: now })
          .eq('id', existing.id);
        if (upErr) throw upErr;
      } else {
        const { error: inErr } = await supabase.from('journal_entries').insert({
          user_id: userId,
          date: outcomeDateStr,
          entry_type_id: OUTCOME_ENTRY_TYPE,
          answers: answersJson,
          created_at: now,
          updated_at: now,
        });
        if (inErr) throw inErr;
      }
      setOutcomeSaved(true);
      setStatus('Outcome recorded.');
      tourBus.emit('clio:saved');
    } catch (e: any) {
      setError(e?.message ?? 'Could not save outcome.');
    } finally {
      setOutcomeSaving(false);
    }
  };

  // Tour: a wait that is already satisfied when its step starts fires at once.
  useEffect(() => {
    if (!ob.active) return;
    if (ob.liveTarget === 'clio.outcome' && outcome) tourBus.emit('clio:outcome');
    else if (ob.liveTarget === 'clio.save' && outcomeSaved) tourBus.emit('clio:saved');
  }, [ob.active, ob.liveTarget, outcome, outcomeSaved]);

  const markItineraryDone = async (item: ItineraryItem) => {
    if (!userId) return;
    try {
      if (item.isTask) {
        const { error: err } = await supabase
          .from('tasks')
          .update({ done: true, updated_at: new Date().toISOString() })
          .eq('id', item.rawId)
          .eq('user_id', userId);
        if (err) throw err;
        setTasksRaw((prev) => prev.filter((t) => t.id !== item.rawId));
      } else {
        const { error: err } = await supabase
          .from('calendar_events')
          .delete()
          .eq('id', item.rawId)
          .eq('user_id', userId);
        if (err) throw err;
        setEventsRaw((prev) => prev.filter((e) => e.id !== item.rawId));
      }
      setStatus(`"${item.title}" marked done.`);
    } catch (e: any) {
      setError(e?.message ?? 'Could not mark item as done.');
    }
  };

  // Reschedule an itinerary item — web reference: journal/page.tsx's
  // `handleItineraryItemReschedule`. Tasks get the intelligent "next open
  // slot" search (the task's OWN group, starting tomorrow) via
  // `rescheduleTaskToNextSlot`; calendar events (no group) open the manual
  // date/time picker instead, matching web's <RescheduleDialog> branch.
  const rescheduleItineraryItem = useCallback(
    async (item: ItineraryItem) => {
      if (!userId) return;
      if (!item.isTask) {
        setEventRescheduleItem(item);
        return;
      }
      setItineraryBusyId(item.id);
      try {
        const result = await rescheduleTaskToNextSlot(userId, item.rawId, groups);
        if (result.status === 'scheduled') {
          setStatus(`"${result.title}" rescheduled to ${result.scheduledTo}.`);
        } else if (result.status === 'unscheduled') {
          setStatus(`"${result.title}" was unscheduled — ${result.reason}.`);
        } else {
          setError(result.message);
        }
        await loadItinerary();
      } finally {
        setItineraryBusyId(null);
      }
    },
    [userId, groups, loadItinerary]
  );

  const openStarted = (item: ItineraryItem) => {
    setStartedItem(item);
    setStartedReason('');
  };

  // web reference: journal/page.tsx's `saveStartedReason` — logs
  // { user_id, item_id, item_type, title, date, status: 'started', reason }
  // to `itinerary_logs` (today's date, NOT the item's scheduled date), then
  // runs the exact same reschedule path as the plain Reschedule action.
  const saveStartedReason = async () => {
    if (!userId || !startedItem) return;
    const item = startedItem;
    try {
      const { error: err } = await supabase.from('itinerary_logs').insert({
        user_id: userId,
        item_id: item.rawId,
        item_type: item.isTask ? 'task' : 'event',
        title: item.title,
        date: ymd(new Date()),
        status: 'started',
        reason: startedReason,
      });
      if (err) throw err;
      setStartedItem(null);
      setStartedReason('');
      await rescheduleItineraryItem(item);
    } catch (e: any) {
      setError(e?.message ?? 'Could not log started reason.');
    }
  };

  // Manual calendar-event reschedule — web reference: journal/page.tsx's
  // `onManualReschedule` (event branch): updates only `date` + `start_time`
  // (end_time is left untouched, matching web exactly).
  const submitEventReschedule = async (dateStr: string, timeStr: string) => {
    if (!userId || !eventRescheduleItem) return;
    const item = eventRescheduleItem;
    try {
      const { error: err } = await supabase
        .from('calendar_events')
        .update({ date: dateStr, start_time: timeStr, updated_at: new Date().toISOString() })
        .eq('id', item.rawId)
        .eq('user_id', userId);
      if (err) throw err;
      setEventRescheduleItem(null);
      setStatus(`"${item.title}" moved to ${dateStr} at ${timeStr}.`);
      await loadItinerary();
    } catch (e: any) {
      setError(e?.message ?? 'Could not reschedule item.');
    }
  };

  // ── Journal entry for the selected date + entry type ─────────────────────
  useEffect(() => {
    if (!userId || !selectedEntryTypeId) {
      setAnswers({});
      setWordCounts({});
      setCurrentEntryId(null);
      setCurrentEntryUpdatedAt(null);
      setAdditionalEntries([]);
      setDraftAdditional([]);
      setExcludeFromEpic(false);
      isDirtyRef.current = false;
      return;
    }

    let cancelled = false;
    (async () => {
      setEntryLoading(true);
      isDirtyRef.current = false;
      const dateStr = ymd(selectedDate);

      const { data, error: err } = await supabase
        .from('journal_entries')
        .select('id, date, entry_type_id, answers, additional_entries, exclude_from_epic, updated_at')
        .eq('user_id', userId)
        .eq('date', dateStr)
        .eq('entry_type_id', selectedEntryTypeId)
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (err) {
        setError(err.message);
        setEntryLoading(false);
        return;
      }

      // Live journal_entries has no legacy `text` column — answers JSON only.
      const dbAnswers: Record<string, string> = data ? (data.answers ?? {}) : {};

      setAnswers(dbAnswers);
      const counts: Record<string, number> = {};
      Object.entries(dbAnswers).forEach(([k, v]) => {
        counts[k] = countWords(typeof v === 'string' ? v : '');
      });
      setWordCounts(counts);
      setCurrentEntryId(data?.id ?? null);
      setCurrentEntryUpdatedAt(data?.updated_at ?? null);
      setAdditionalEntries(Array.isArray(data?.additional_entries) ? data.additional_entries : []);
      setDraftAdditional([]);
      setExcludeFromEpic(!!data?.exclude_from_epic);
      isDirtyRef.current = false;
      setError(null);
      setEntryLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, selectedDate, selectedEntryTypeId, entryTypes]);

  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(null), 2500);
    return () => clearTimeout(t);
  }, [status]);

  const handleAnswerChange = useCallback((promptId: string, value: string) => {
    isDirtyRef.current = true;
    setAnswers((prev) => ({ ...prev, [promptId]: value }));
    setWordCounts((prev) => ({ ...prev, [promptId]: countWords(value) }));
  }, []);

  const navigateDay = (direction: 'prev' | 'next') => {
    setSelectedDate((prev) => atMidnight(addDays(prev, direction === 'prev' ? -1 : 1)));
  };

  const handleSaveEntry = async () => {
    if (!userId || !selectedEntryTypeId || saving) return;
    setSaving(true);
    setError(null);
    const dateStr = ymd(selectedDate);
    const now = new Date().toISOString();
    const currentAnswers = answers;

    try {
      if (currentEntryId) {
        const { data: currentRow, error: fetchErr } = await supabase
          .from('journal_entries')
          .select('updated_at')
          .eq('id', currentEntryId)
          .single();

        if (fetchErr) {
          setError('This entry may have been deleted. Pull the day again to refresh.');
        } else {
          const dbTime = currentRow?.updated_at ? new Date(currentRow.updated_at).getTime() : 0;
          const localTime = currentEntryUpdatedAt ? new Date(currentEntryUpdatedAt).getTime() : 0;
          if (dbTime > localTime) {
            setError('This entry was updated elsewhere. Refresh before saving again.');
          } else {
            const { error: updateErr } = await supabase
              .from('journal_entries')
              .update({
                user_id: userId,
                date: dateStr,
                entry_type_id: selectedEntryTypeId,
                answers: currentAnswers,
                exclude_from_epic: excludeFromEpic,
                updated_at: now,
              })
              .eq('id', currentEntryId);
            if (updateErr) throw updateErr;
            isDirtyRef.current = false;
            setCurrentEntryUpdatedAt(now);
            setStatus('Journal entry updated.');
          }
        }
      } else {
        const { data: inserted, error: insertErr } = await supabase
          .from('journal_entries')
          .insert({
            user_id: userId,
            date: dateStr,
            entry_type_id: selectedEntryTypeId,
            answers: currentAnswers,
            exclude_from_epic: excludeFromEpic,
            created_at: now,
            updated_at: now,
          })
          .select('id, updated_at')
          .single();
        if (insertErr) throw insertErr;
        isDirtyRef.current = false;
        setCurrentEntryId(inserted.id);
        setCurrentEntryUpdatedAt(inserted.updated_at);
        setStatus('Journal entry saved.');
      }
    } catch (e: any) {
      setError(e?.message ?? 'Could not save journal entry.');
    } finally {
      setSaving(false);
    }
  };

  // ── Additional entries (only once the main entry is saved) ──────────────
  const addDraftAdditional = () => {
    setDraftAdditional((prev) => [...prev, { id: genId('draft'), text: '' }]);
  };

  const changeDraftAdditional = (id: string, text: string) => {
    setDraftAdditional((prev) => prev.map((d) => (d.id === id ? { ...d, text } : d)));
  };

  const saveDraftAdditional = async (draftId: string) => {
    if (!userId || !currentEntryId) return;
    const draft = draftAdditional.find((d) => d.id === draftId);
    if (!draft || !draft.text.trim()) return;
    setSavingAdditionalId(draftId);
    const now = new Date().toISOString();
    const entry: AdditionalJournalEntry = { id: genId('add'), text: draft.text, createdAt: now, updatedAt: now };
    const next = [...additionalEntries, entry];
    const { error: err } = await supabase
      .from('journal_entries')
      .update({ additional_entries: next, updated_at: now })
      .eq('id', currentEntryId);
    if (err) {
      setError(err.message);
    } else {
      setAdditionalEntries(next);
      setDraftAdditional((prev) => prev.filter((d) => d.id !== draftId));
      setStatus('Additional entry saved.');
    }
    setSavingAdditionalId(null);
  };

  const changeAdditionalEntry = (id: string, text: string) => {
    setAdditionalEntries((prev) => prev.map((e) => (e.id === id ? { ...e, text } : e)));
  };

  const saveAdditionalEntry = async (id: string) => {
    if (!userId || !currentEntryId) return;
    setSavingAdditionalId(id);
    const now = new Date().toISOString();
    const next = additionalEntries.map((e) => (e.id === id ? { ...e, updatedAt: now } : e));
    const { error: err } = await supabase
      .from('journal_entries')
      .update({ additional_entries: next, updated_at: now })
      .eq('id', currentEntryId);
    if (err) {
      setError(err.message);
    } else {
      setAdditionalEntries(next);
      setStatus('Additional entry updated.');
    }
    setSavingAdditionalId(null);
  };

  // ── TKDT (MoyRi > TRO: Degrade counters, surfaced on the Journal page) ───
  const persistTkdt = async (next: TKDTItem[]) => {
    setTkdtItems(next);
    if (!userId) return;
    const { error: err } = await supabase.from('user_settings').update({ tkdt_items: next }).eq('user_id', userId);
    if (err) setError(err.message);
  };

  const incrementTkdt = (id: string) => {
    void persistTkdt(tkdtItems.map((i) => (i.id === id ? { ...i, count: i.count + 1 } : i)));
  };

  const addTkdtItem = () => {
    const label = tkdtNewLabel.trim();
    if (!label) return;
    setTkdtNewLabel('');
    void persistTkdt([...tkdtItems, { id: genId('tkdt'), label, count: 0 }]);
  };

  // ── Derived view state ───────────────────────────────────────────────────
  const selectedType = useMemo(
    () => entryTypes.find((t) => t.id === selectedEntryTypeId),
    [entryTypes, selectedEntryTypeId]
  );

  const showWordCount = selectedType?.wordCountSettings?.showWordCount ?? false;
  const useWordCountTarget = selectedType?.wordCountSettings?.useWordCountTarget ?? false;
  const wordCountTarget = selectedType?.wordCountSettings?.wordCountTarget ?? 0;
  const totalWordCount = useMemo(() => Object.values(wordCounts).reduce((sum, c) => sum + c, 0), [wordCounts]);
  const isWordCountTargetMet = useWordCountTarget && wordCountTarget > 0 && totalWordCount >= wordCountTarget;

  const guidingPrinciples = useMemo(() => {
    const show = selectedType?.showGuidingPrinciples ?? globalShowGuidingPrinciples;
    if (!show) return [];
    const ids = selectedType?.guidingPrincipleIds ?? globalGuidingPrincipleIds;
    if (!ids || ids.length === 0) return [];
    return computeGuidingPrinciples(ids, telosDomains);
  }, [selectedType, globalShowGuidingPrinciples, globalGuidingPrincipleIds, telosDomains]);

  // Web reference: journal/page.tsx guiding-principles block's `font` merge
  // (`{ ...DEFAULT_GUIDING_PRINCIPLE_FONT, ...(selectedType?.guidingPrincipleFont
  // || settings.guidingPrincipleFont || {}) }`), mapped to RN Text style props.
  const guidingPrincipleFont: GuidingPrincipleFont = useMemo(() => {
    return {
      ...DEFAULT_GUIDING_PRINCIPLE_FONT,
      color: c.textFaint,
      ...(selectedType?.guidingPrincipleFont ?? globalGuidingPrincipleFont ?? {}),
    };
  }, [selectedType, globalGuidingPrincipleFont, c.textFaint]);

  const principleTextStyle = useMemo(
    () => ({
      fontFamily: guidingPrincipleFont.fontFamily,
      fontSize: guidingPrincipleFont.fontSize,
      color: guidingPrincipleFont.color,
      fontWeight: guidingPrincipleFont.bold ? ('700' as const) : ('400' as const),
      fontStyle: guidingPrincipleFont.italic ? ('italic' as const) : ('normal' as const),
      textDecorationLine: guidingPrincipleFont.underline ? ('underline' as const) : ('none' as const),
    }),
    [guidingPrincipleFont]
  );

  const goalsToShow = useMemo(() => {
    if (!selectedType?.showGoals) return { monthly: [] as MonthlyGoal[], weekly: [] as WeeklyGoal[] };
    const ids = selectedType.goalIds ?? [];
    if (ids.length === 0) return { monthly: [] as MonthlyGoal[], weekly: [] as WeeklyGoal[] };
    return {
      monthly: monthlyGoals.filter((g) => ids.includes(g.id) && g.text.trim() !== ''),
      weekly: weeklyGoals.filter((g) => ids.includes(g.id) && g.text.trim() !== ''),
    };
  }, [selectedType, monthlyGoals, weeklyGoals]);

  const showTKDTSection = selectedType?.showTKDT ?? globalShowTKDT;
  const showItinerarySection = selectedType?.showItinerary ?? false;
  const showAdditional = !!selectedType?.additionalEntryEnabled && !!currentEntryId;

  // ── TELOS domain hit/miss cards (matches web's `showTelosDomainCards`) ───
  // Web reference: journal/page.tsx's `showTelosDomainCards` / `telosActiveDomains`
  // / `telosDomainStates` load effect + `markTelosDomain`. Writes the SAME
  // `telos_state` row TelosTool.tsx reads/writes (user_id, cycle, domains).
  const showTelosDomainCardsSection = selectedType?.showTelosDomainCards ?? globalShowTelosDomainCards;
  const telosActiveDomains = useMemo(() => telosDomains.filter((d) => d.isActive), [telosDomains]);
  const [telosLoading, setTelosLoading] = useState(true);
  const telosSaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showTelosDomainCardsSection || !userId) {
      setTelosLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setTelosLoading(true);
      const { data, error: err } = await supabase
        .from('telos_state')
        .select('cycle, domains')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        setTelosCycle(data?.cycle ?? 0);
        setTelosDomainStates(data?.domains ?? {});
      }
      setTelosLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showTelosDomainCardsSection, userId]);

  // Ensure every active domain has state — web reference: journal/page.tsx's
  // "Ensure all active domains have state" effect.
  useEffect(() => {
    if (telosLoading || !showTelosDomainCardsSection) return;
    setTelosDomainStates((prev) => {
      const next = { ...prev };
      let changed = false;
      telosActiveDomains.forEach((d) => {
        if (!next[d.id]) {
          next[d.id] = createDefaultTelosDomainState();
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [telosActiveDomains, telosLoading, showTelosDomainCardsSection]);

  useEffect(() => {
    return () => {
      if (telosSaveTimeout.current) clearTimeout(telosSaveTimeout.current);
    };
  }, []);

  // Debounced upsert into `telos_state` — same table/shape as TelosTool.tsx's
  // `saveTelosState`, so a pending mark set here is visible there too.
  const saveTelosState = useCallback(
    (newCycle: number, newStates: Record<string, TelosDomainState>) => {
      if (!userId) return;
      if (telosSaveTimeout.current) clearTimeout(telosSaveTimeout.current);
      telosSaveTimeout.current = setTimeout(async () => {
        const { error: err } = await supabase.from('telos_state').upsert({
          user_id: userId,
          cycle: newCycle,
          domains: newStates,
          updated_at: new Date().toISOString(),
        });
        if (err) setError(err.message);
      }, 800);
    },
    [userId]
  );

  // Web reference: journal/page.tsx's `markTelosDomain` — sets the domain's
  // `pending` mark locally, then persists to `telos_state` (debounced, same
  // as the cycle save path TelosTool.tsx uses).
  const markTelosDomain = useCallback(
    (domainId: string, action: 'hit' | 'miss') => {
      setTelosDomainStates((prev) => {
        const next = {
          ...prev,
          [domainId]: { ...(prev[domainId] || createDefaultTelosDomainState()), pending: action },
        };
        saveTelosState(telosCycle, next);
        return next;
      });
    },
    [saveTelosState, telosCycle]
  );

  // ── Auto-schedule verification bell (matches web's `showAutoVerification`) ─
  const showAutoVerification = selectedType?.showAutoVerification ?? false;

  const refreshAutoScheduleLogs = useCallback(async () => {
    if (!userId) return;
    setAutoScheduleLogs(await fetchAutoScheduleLogs(userId));
  }, [userId]);

  useEffect(() => {
    if (!showAutoVerification || !userId) {
      setAutoScheduleLogs([]);
      return;
    }
    void refreshAutoScheduleLogs();
  }, [showAutoVerification, userId, refreshAutoScheduleLogs]);

  if (settingsLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={styles.root}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
    >
      <Atmosphere />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={styles.helpRow}>
          <ScreenHelp help={clioHelp} />
        </View>
        <View style={styles.titleRow}>
          <Text style={styles.title}>My Journal</Text>
          {showAutoVerification && (
            <Pressable
              style={styles.bellButton}
              onPress={() => setVerificationOpen(true)}
              accessibilityLabel="Auto-schedule verification"
            >
              <Text style={styles.bellIcon}>🔔</Text>
              {autoScheduleLogs.length > 0 && (
                <View style={styles.bellBadge}>
                  <Text style={styles.bellBadgeText}>{autoScheduleLogs.length}</Text>
                </View>
              )}
            </Pressable>
          )}
        </View>

        {error ? <Text style={styles.error}>{error}</Text> : null}
        {status ? <Text style={styles.status}>{status}</Text> : null}

        {/* Date navigator */}
        <View style={styles.dateNav}>
<GlassFill radius={radii.tile} />
          <Pressable
            style={styles.navArrow}
            onPress={() => navigateDay('prev')}
            disabled={entryLoading}
            accessibilityLabel="Previous day"
          >
            <Text style={styles.navArrowText}>‹</Text>
          </Pressable>
          <Text style={styles.dateHeading}>{formatLongDate(selectedDate)}</Text>
          <Pressable
            style={styles.navArrow}
            onPress={() => navigateDay('next')}
            disabled={entryLoading}
            accessibilityLabel="Next day"
          >
            <Text style={styles.navArrowText}>›</Text>
          </Pressable>
        </View>

        {/* Day outcome review */}
        <View style={styles.outcomeBlock}>
<GlassFill radius={radii.panel} />
          <Text style={styles.sectionHeading}>How did it go?</Text>
          <TourBox id="clio.outcome">
            <View style={styles.outcomeRow}>
              {OUTCOME_OPTIONS.map((o) => {
                const active = outcome === o.key;
                return (
                  <Pressable
                    key={o.key}
                    style={[styles.outcomeChip, active && styles.entryTypeChipActive]}
                    onPress={() => {
                      setOutcome(o.key);
                      setOutcomeSaved(false);
                      tourBus.emit('clio:outcome');
                    }}
                  >
                    <Text style={[styles.entryTypeChipText, active && styles.entryTypeChipTextActive]}>{o.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </TourBox>
          {outcome ? (
            <>
              <TourBox id="clio.note">
                <TextInput
                  style={styles.tkdtInput}
                  placeholder="Optional note…"
                  placeholderTextColor={c.slate}
                  value={outcomeNote}
                  onChangeText={(v) => { setOutcomeNote(v); setOutcomeSaved(false); }}
                  onSubmitEditing={() => tourBus.emit('clio:note')}
                  returnKeyType="done"
                />
              </TourBox>
              <View style={styles.outcomeRow}>
                <TourBox id="clio.note">
                  <Pressable
                    style={styles.outcomeChip}
                    onPress={() => { setOutcomeNote(''); tourBus.emit('clio:note'); }}
                  >
                    <Text style={styles.entryTypeChipText}>Skip note</Text>
                  </Pressable>
                </TourBox>
                <TourBox id="clio.save">
                  <PrimaryButton
                    small
                    icon={<CheckCircleIcon />}
                    label={outcomeSaving ? 'Saving…' : outcomeSaved ? 'Saved' : 'Save'}
                    onPress={() => void saveOutcome()}
                    disabled={outcomeSaving}
                  />
                </TourBox>
              </View>
            </>
          ) : null}
        </View>

        {/* EPIC Section */}
        <View style={styles.epicBlock}>
          <Text style={styles.epicSectionTitle}>EPIC</Text>
          {epicLoading ? (
            <View style={styles.epicLoadingContainer}>
              <ActivityIndicator color={c.blueText} size="small" />
              <Text style={styles.epicLoadingText}>Loading episode…</Text>
            </View>
          ) : epicError ? (
            <Text style={styles.epicErrorText}>{epicError}</Text>
          ) : epicEpisode ? (
            <View style={styles.epicCard}>
<GlassFill radius={radii.panel} />
              <View style={styles.epicCardHeader}>
                <Text style={styles.epicEpisodeNumber}>
                  Episode {epicEpisode.episodeNumber}
                </Text>
                <Text style={styles.epicEpisodeTitle}>{epicEpisode.title}</Text>
              </View>

              {/* Audio player or generation */}
              <View style={styles.epicAudioSection}>
                {epicEpisode.audioPath ? (
                  <Pressable
                    style={[styles.epicPlayButton, epicAudioPlaying && styles.epicPlayButtonActive]}
                    onPress={epicPlayAudio}
                  >
                    <Text style={styles.epicPlayButtonText}>
                      {epicAudioPlaying ? 'Pause' : 'Play'}
                    </Text>
                  </Pressable>
                ) : (
                  <Pressable
                    style={[
                      styles.epicGenerateAudioButton,
                      epicAudioGenerating && styles.epicGenerateAudioButtonDisabled,
                    ]}
                    onPress={epicGenerateAudio}
                    disabled={epicAudioGenerating}
                  >
                    <Text style={styles.epicGenerateAudioButtonText}>
                      {epicAudioGenerating ? 'Generating…' : 'Generate audio'}
                    </Text>
                  </Pressable>
                )}
              </View>

              {/* Transcript */}
              {(epicEpisode.editedScript || epicEpisode.script) && (
                <View style={styles.epicTranscriptContainer}>
                  <Text style={styles.epicTranscriptText}>
                    {epicEpisode.editedScript || epicEpisode.script}
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <PrimaryButton
              icon={<SparkleIcon />}
              label={epicLoading ? 'Generating…' : "Generate today's episode"}
              onPress={epicGenerateEpisode}
              disabled={epicLoading}
            />
          )}
        </View>

        {/* Guiding principles — a "quote card": sky + moon behind the text,
            matching the heavenly reference look rather than plain italic
            lines on the tool's flat background. */}
        {guidingPrinciples.length > 0 && (
          <View style={styles.principlesBlock}>
            <CloudBackdrop style={{ borderRadius: radii.panel }} />
            <MoonGlow side="right" />
            {guidingPrinciples.map((text, i) => (
              <Text key={i} style={[styles.principleText, principleTextStyle]}>
                {text}
              </Text>
            ))}
          </View>
        )}

        {/* Goals (monthly + weekly) */}
        {(goalsToShow.monthly.length > 0 || goalsToShow.weekly.length > 0) && (
          <View style={styles.goalsBlock}>
            {goalsToShow.monthly.length > 0 && (
              <View style={styles.goalsGroup}>
                <Text style={styles.goalsLabel}>Monthly Goals</Text>
                {goalsToShow.monthly.map((g) => (
                  <View key={g.id} style={styles.goalRow}>
                    <View style={styles.goalDot} />
                    <Text style={[styles.goalText, g.completed && styles.goalTextDone]}>{g.text}</Text>
                  </View>
                ))}
              </View>
            )}
            {goalsToShow.weekly.length > 0 && (
              <View style={styles.goalsGroup}>
                <Text style={styles.goalsLabel}>Weekly Goals</Text>
                {goalsToShow.weekly.map((g) => (
                  <View key={g.id} style={styles.goalRow}>
                    <View style={styles.goalDot} />
                    <Text style={[styles.goalText, g.completed && styles.goalTextDone]}>{g.text}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* TKDT */}
        {showTKDTSection && (
          <View style={styles.tkdtBlock}>
            <Pressable style={styles.tkdtToggle} onPress={() => setTkdtOpen((v) => !v)}>
              <Text style={styles.tkdtToggleText}>TKDT {tkdtOpen ? '▾' : '▸'}</Text>
            </Pressable>
            {tkdtOpen && (
              <View style={styles.tkdtPanel}>
<GlassFill radius={radii.tile} />
                {tkdtItems.length === 0 ? (
                  <Text style={styles.emptyInline}>No TKDT items yet.</Text>
                ) : (
                  tkdtItems.map((item) => (
                    <Pressable key={item.id} style={styles.tkdtRow} onPress={() => incrementTkdt(item.id)}>
                      <Text style={styles.tkdtLabel}>{item.label}</Text>
                      <Text style={styles.tkdtCount}>{item.count}</Text>
                    </Pressable>
                  ))
                )}
                <View style={styles.tkdtAddRow}>
                  <TextInput
                    style={styles.tkdtInput}
                    placeholder="New item name…"
                    placeholderTextColor={c.slate}
                    value={tkdtNewLabel}
                    onChangeText={setTkdtNewLabel}
                    onSubmitEditing={addTkdtItem}
                    returnKeyType="done"
                  />
                  <Pressable style={styles.tkdtAddButton} onPress={addTkdtItem}>
                    <Text style={styles.tkdtAddText}>+</Text>
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Today's Itinerary */}
        {showItinerarySection && (
          <View style={styles.itineraryBlock}>
            <Text style={styles.sectionHeading}>Today's Itinerary</Text>
            {itineraryLoading ? (
              <Text style={styles.emptyInline}>Loading itinerary…</Text>
            ) : itineraryItems.length > 0 ? (
              itineraryItems.map((item) => {
                const busy = itineraryBusyId === item.id;
                return (
                  <View key={item.id} style={styles.itineraryRow}>
<GlassFill radius={radii.tile} />
                    <View style={styles.itineraryHeaderRow}>
                      <Text style={styles.itineraryTime}>{item.startTime}</Text>
                      <Text style={styles.itineraryTitle} numberOfLines={2}>
                        {item.title}
                      </Text>
                    </View>
                    <View style={styles.itineraryActions}>
                      <PrimaryButton
                        small
                        icon={<CheckCircleIcon />}
                        label="Done"
                        onPress={() => void markItineraryDone(item)}
                        disabled={busy}
                        style={{ flexGrow: 1 }}
                      />
                      <GlassPill
                        icon={<PlayCircleIcon />}
                        label="Started"
                        onPress={() => openStarted(item)}
                        disabled={busy}
                        style={{ flexGrow: 1 }}
                      />
                      <GlassPill
                        icon={<CalendarIcon />}
                        label="Reschedule"
                        onPress={() => void rescheduleItineraryItem(item)}
                        disabled={busy}
                        style={{ flexGrow: 1 }}
                      />
                    </View>
                  </View>
                );
              })
            ) : (
              <Text style={styles.emptyInline}>Nothing scheduled for today.</Text>
            )}
          </View>
        )}

        {/* TELOS domain hit/miss cards */}
        {showTelosDomainCardsSection && telosActiveDomains.length > 0 && (
          <View style={styles.telosBlock}>
            <Text style={styles.sectionHeading}>
              T.E.L.O.S <Text style={styles.telosCycleLabel}>Cycle {telosCycle}</Text>
            </Text>
            {telosLoading ? (
              <Text style={styles.emptyInline}>Loading domains…</Text>
            ) : (
              <View style={styles.telosCardRow}>
                {telosActiveDomains.map((d) => {
                  const st = telosDomainStates[d.id] || createDefaultTelosDomainState();
                  return (
                    <View key={d.id} style={styles.telosCard}>
<GlassFill radius={radii.tile} />
                      <Text style={[styles.telosCardTitle, { color: d.color }]} numberOfLines={1}>
                        {d.name}
                      </Text>
                      <Pressable
                        style={[
                          styles.telosMarkBtn,
                          { backgroundColor: st.pending === 'hit' ? d.color : t.onPace },
                        ]}
                        onPress={() => markTelosDomain(d.id, 'hit')}
                      >
                        <Text style={styles.telosMarkBtnText}>Did the work</Text>
                      </Pressable>
                      <Pressable
                        style={[
                          styles.telosMarkBtn,
                          { backgroundColor: st.pending === 'miss' ? t.lapsed : t.navyRaised },
                        ]}
                        onPress={() => markTelosDomain(d.id, 'miss')}
                      >
                        <Text
                          style={[styles.telosMarkBtnText, st.pending !== 'miss' && styles.telosMarkBtnTextMuted]}
                        >
                          Missed it
                        </Text>
                      </Pressable>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        <View style={styles.divider} />

        {/* Entry type selector (AM/PM etc.) */}
        {entryTypes.length > 0 ? (
          <View style={styles.entryTypeRow}>
            {entryTypes.map((type) => {
              const active = type.id === selectedEntryTypeId;
              return (
                <Pressable
                  key={type.id}
                  style={[styles.entryTypeChip, active && styles.entryTypeChipActive]}
                  onPress={() => selectEntryType(type.id)}
                  disabled={entryLoading}
                >
                  <Text style={[styles.entryTypeChipText, active && styles.entryTypeChipTextActive]}>
                    {type.title}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ) : (
          <Text style={styles.emptyInline}>Please configure journal entry types in the Settings page to begin.</Text>
        )}

        {/* Prompts */}
        {selectedType ? (
          <View style={[styles.promptsPanel, useWordCountTarget && styles.promptsPanelBordered]}>
<GlassFill radius={radii.panel} />
            <View style={styles.promptsHeaderRow}>
              <Text style={styles.sectionHeading}>Prompts for {selectedType.title}:</Text>
              <View style={styles.promptsHeaderRightRow}>
                {showWordCount && (
                  <View style={[styles.wordBadge, isWordCountTargetMet && styles.wordBadgeMet]}>
                    <Text style={[styles.wordBadgeText, isWordCountTargetMet && styles.wordBadgeTextMet]}>
                      {totalWordCount}
                      {useWordCountTarget ? ` / ${wordCountTarget}` : ''} words
                    </Text>
                  </View>
                )}
                <Pressable
                  style={[styles.epBadge, !excludeFromEpic && styles.epBadgeActive]}
                  onPress={() => setExcludeFromEpic((prev) => !prev)}
                  disabled={entryLoading}
                >
                  <Text style={[styles.epBadgeText, !excludeFromEpic && styles.epBadgeTextActive]}>EP</Text>
                </Pressable>
              </View>
            </View>
            {excludeFromEpic && (
              <Text style={styles.emptyInline}>Private — excluded from EPIC episode content.</Text>
            )}

            {selectedType.prompts.length > 0 ? (
              selectedType.prompts.map((prompt) =>
                prompt.type === 'text' ? (
                  <View key={prompt.id} style={styles.promptCard}>
                    <Text style={styles.promptLabel}>{prompt.text}</Text>
                    <TextInput
                      style={styles.promptInput}
                      multiline
                      textAlignVertical="top"
                      placeholder="Your response…"
                      placeholderTextColor={c.slate}
                      value={answers[prompt.id] ?? ''}
                      onChangeText={(v) => handleAnswerChange(prompt.id, v)}
                      editable={!entryLoading}
                    />
                  </View>
                ) : (
                  <View key={prompt.id} style={styles.promptCard}>
                    <Text style={styles.promptLabel}>{prompt.text}</Text>
                    <View style={styles.optionRow}>
                      {(prompt.options ?? []).map((option) => {
                        const active = (answers[prompt.id] ?? '') === option;
                        return (
                          <Pressable
                            key={option}
                            style={[styles.optionChip, active && styles.optionChipActive]}
                            onPress={() => handleAnswerChange(prompt.id, option)}
                            disabled={entryLoading}
                          >
                            <Text style={[styles.optionChipText, active && styles.optionChipTextActive]}>
                              {option}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                )
              )
            ) : (
              <Text style={styles.emptyInline}>No specific prompts for this entry type. Free write!</Text>
            )}
          </View>
        ) : entryTypes.length > 0 ? (
          <Text style={styles.emptyInline}>Select an entry type above to see prompts and write your journal.</Text>
        ) : null}

        <PrimaryButton
          label={saving ? 'Saving…' : currentEntryId ? 'Save Changes' : 'Save Entry'}
          onPress={handleSaveEntry}
          disabled={entryLoading || saving || !selectedEntryTypeId}
        />

        {/* Additional entries */}
        {showAdditional && (
          <View style={styles.additionalBlock}>
            <View style={styles.divider} />
            <View style={styles.additionalHeaderRow}>
              <Text style={styles.sectionHeadingSm}>Additional entries</Text>
              <Pressable style={styles.additionalAddButton} onPress={addDraftAdditional}>
                <Text style={styles.additionalAddText}>+ ADDITIONAL</Text>
              </Pressable>
            </View>

            {additionalEntries.length === 0 && draftAdditional.length === 0 && (
              <Text style={styles.emptyInline}>No additional entries yet. Tap ADDITIONAL to add one.</Text>
            )}

            {additionalEntries.map((entry) => {
              const created = new Date(entry.createdAt);
              const createdLabel = Number.isNaN(created.getTime()) ? entry.createdAt : created.toLocaleString();
              const showEdited = entry.updatedAt !== entry.createdAt;
              const updated = new Date(entry.updatedAt);
              const updatedLabel = Number.isNaN(updated.getTime()) ? entry.updatedAt : updated.toLocaleString();
              const busy = savingAdditionalId === entry.id;
              return (
                <View key={entry.id} style={styles.additionalCard}>
<GlassFill radius={radii.tile} />
                  <Text style={styles.additionalMeta}>
                    {createdLabel}
                    {showEdited ? ` (edited ${updatedLabel})` : ''}
                  </Text>
                  <TextInput
                    style={styles.additionalInput}
                    multiline
                    textAlignVertical="top"
                    placeholder="Your additional thoughts…"
                    placeholderTextColor={c.slate}
                    value={entry.text}
                    onChangeText={(v) => changeAdditionalEntry(entry.id, v)}
                    editable={!busy}
                  />
                  <Pressable
                    style={[styles.additionalSaveButton, busy && styles.saveButtonDisabled]}
                    onPress={() => void saveAdditionalEntry(entry.id)}
                    disabled={busy}
                  >
                    <Text style={styles.additionalSaveText}>{busy ? 'Saving…' : 'Save'}</Text>
                  </Pressable>
                </View>
              );
            })}

            {draftAdditional.map((draft) => {
              const busy = savingAdditionalId === draft.id;
              return (
                <View key={draft.id} style={[styles.additionalCard, styles.additionalCardDraft]}>
<GlassFill radius={radii.tile} />
                  <Text style={styles.additionalMetaDraft}>Unsaved draft</Text>
                  <TextInput
                    style={styles.additionalInput}
                    multiline
                    textAlignVertical="top"
                    placeholder="Your additional thoughts…"
                    placeholderTextColor={c.slate}
                    value={draft.text}
                    onChangeText={(v) => changeDraftAdditional(draft.id, v)}
                    editable={!busy}
                    autoFocus
                  />
                  <Pressable
                    style={[styles.additionalSaveButton, (busy || !draft.text.trim()) && styles.saveButtonDisabled]}
                    onPress={() => void saveDraftAdditional(draft.id)}
                    disabled={busy || !draft.text.trim()}
                  >
                    <Text style={styles.additionalSaveText}>{busy ? 'Saving…' : 'Save'}</Text>
                  </Pressable>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      <VerificationModal
        visible={verificationOpen}
        logs={autoScheduleLogs}
        view={verificationView}
        onChangeView={setVerificationView}
        onClose={() => setVerificationOpen(false)}
        onKeep={(log) => {
          void keepAutoScheduleLog(log.id).then(() => {
            setAutoScheduleLogs((prev) => prev.filter((l) => l.id !== log.id));
          });
        }}
        onKeepAll={() => {
          const ids = autoScheduleLogs.map((l) => l.id);
          void keepAllAutoScheduleLogs(ids).then(() => setAutoScheduleLogs([]));
        }}
        onReschedule={(log) => setRescheduleLogItem(log)}
      />

      <LogRescheduleModal
        log={rescheduleLogItem}
        onCancel={() => setRescheduleLogItem(null)}
        onSubmit={(dateVal, timeVal) => {
          if (!userId || !rescheduleLogItem) return;
          const scheduledTo = `${dateVal} ${timeVal}`;
          void manuallyRescheduleLoggedTask(rescheduleLogItem, userId, scheduledTo).then(() => {
            setAutoScheduleLogs((prev) => prev.filter((l) => l.id !== rescheduleLogItem.id));
            setStatus(`"${rescheduleLogItem.task_title}" rescheduled to ${scheduledTo}.`);
            setRescheduleLogItem(null);
          });
        }}
      />

      <StartedReasonModal
        item={startedItem}
        reason={startedReason}
        onChangeReason={setStartedReason}
        onCancel={() => setStartedItem(null)}
        onSubmit={() => void saveStartedReason()}
      />

      <ItineraryRescheduleModal
        item={eventRescheduleItem}
        defaultDate={selectedDate}
        onCancel={() => setEventRescheduleItem(null)}
        onSubmit={(dateStr, timeStr) => void submitEventReschedule(dateStr, timeStr)}
      />
    </KeyboardAvoidingView>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1 },
  scrollContent: { padding: 14, paddingBottom: 32, gap: 12 },
  title: { fontSize: 22, fontWeight: '800', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  titleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
  error: { color: c.danger, fontSize: 13 },
  status: { color: c.blueText, fontSize: 13, fontWeight: '700' },

  outcomeBlock: { gap: 8, padding: 12, borderRadius: radii.panel },
  outcomeRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  outcomeChip: { borderRadius: radii.pill, backgroundColor: c.glassFill, paddingHorizontal: 16, paddingVertical: 8, ...neu.raisedSm },
  dateNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    paddingVertical: 8,
    paddingHorizontal: 6,
    ...neu.raised,
  },
  navArrow: { paddingHorizontal: 14, paddingVertical: 6 },
  navArrowText: { fontSize: 22, fontWeight: '900', color: c.ink },
  dateHeading: { fontSize: 16, fontWeight: '800', color: c.ink },

  principlesBlock: {
    gap: 4,
    padding: 16,
    borderRadius: radii.panel,
    overflow: 'hidden',
    ...neu.raised,
  },
  principleText: { fontSize: 14, fontStyle: 'italic', color: c.inkSoft },

  goalsBlock: { gap: 10 },
  goalsGroup: { gap: 3 },
  goalsLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '800',
    color: c.inkSoft,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  goalRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
  goalDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: c.inkSoft, marginTop: 7 },
  goalText: { flex: 1, fontSize: 14, color: c.ink },
  goalTextDone: { textDecorationLine: 'line-through', color: c.inkSoft },

  tkdtBlock: { gap: 6 },
  tkdtToggle: { alignSelf: 'flex-start' },
  tkdtToggleText: { fontSize: 13, fontWeight: '800', color: c.blueText },
  tkdtPanel: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    padding: 10,
    gap: 6,
    ...neu.raised,
  },
  tkdtRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 },
  tkdtLabel: { fontSize: 14, color: c.text, flex: 1 },
  tkdtCount: { fontSize: 13, fontWeight: '800', color: c.blueText, fontVariant: ['tabular-nums'] },
  tkdtAddRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  tkdtInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 13,
    color: c.ink,
    ...neu.insetDeep,
  },
  tkdtAddButton: { backgroundColor: c.primary, borderRadius: radii.tile, paddingHorizontal: 14, justifyContent: 'center' },
  tkdtAddText: { fontWeight: '900', color: c.white },

  itineraryBlock: { gap: 8 },
  itineraryRow: {
    gap: 8,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    padding: 10,
    ...neu.raised,
  },
  itineraryHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  itineraryTime: { fontSize: 12, fontWeight: '700', color: c.inkSoft, width: 48 },
  itineraryTitle: { flex: 1, fontSize: 14, color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  itineraryActions: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },

  // TELOS domain hit/miss cards.
  telosBlock: { gap: 8 },
  telosCycleLabel: { fontSize: 13, fontWeight: '600', color: c.slate },
  telosCardRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  telosCard: {
    flexGrow: 1,
    flexBasis: '47%',
    gap: 6,
    alignItems: 'center',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    padding: 10,
    ...neu.raisedSm,
  },
  telosCardTitle: { fontSize: 13, fontWeight: '900', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  telosMarkBtn: {
    alignSelf: 'stretch',
    alignItems: 'center',
    borderRadius: radii.tile,
    paddingVertical: 8,
  },
  telosMarkBtnText: { fontSize: 12, fontWeight: '800', color: c.white },
  telosMarkBtnTextMuted: { color: c.inkSoft },

  divider: { height: 1, backgroundColor: c.glassRim },

  entryTypeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  entryTypeChip: {
    borderRadius: radii.pill,
    backgroundColor: c.glassFill,
    paddingHorizontal: 16,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  entryTypeChipActive: { backgroundColor: c.primary, ...neu.glowPrimary },
  entryTypeChipText: { fontSize: 14, fontWeight: '700', color: c.ink },
  entryTypeChipTextActive: { color: c.white },

  promptsPanel: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.panel,
    padding: 14,
    gap: 12,
    ...neu.raised,
  },
  promptsPanelBordered: { borderWidth: 1, borderColor: c.blue },
  promptsHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  promptsHeaderRightRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionHeading: { fontSize: 17, fontWeight: '800', color: c.ink },
  sectionHeadingSm: { fontSize: 15, fontWeight: '800', color: c.ink },

  wordBadge: {
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
    ...neu.raisedSm,
  },
  wordBadgeMet: { backgroundColor: c.success },
  wordBadgeText: { fontSize: 12, fontWeight: '800', color: c.ink },
  wordBadgeTextMet: { color: c.white },

  epBadge: {
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
    ...neu.raisedSm,
  },
  epBadgeActive: { backgroundColor: c.primary },
  epBadgeText: { fontSize: 12, fontWeight: '800', color: c.inkSoft },
  epBadgeTextActive: { color: c.white },

  promptCard: { gap: 6 },
  promptLabel: { fontSize: 14, fontWeight: '700', color: c.ink },
  promptInput: {
    minHeight: 100,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: c.ink,
    ...neu.insetDeep,
  },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  optionChip: {
    borderRadius: radii.pill,
    backgroundColor: c.glassFill,
    paddingHorizontal: 14,
    paddingVertical: 7,
    ...neu.raisedSm,
  },
  optionChipActive: { backgroundColor: c.lime, ...neu.inset },
  optionChipText: { fontSize: 13, fontWeight: '700', color: c.ink },
  optionChipTextActive: { color: c.blueText },

  emptyInline: { fontSize: 13, color: c.inkSoft, fontStyle: 'italic' },

  saveButtonDisabled: { opacity: 0.5 },

  additionalBlock: { gap: 10 },
  additionalHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  additionalAddButton: {
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: c.glassFill,
    ...neu.raisedSm,
  },
  additionalAddText: { fontSize: 12, fontWeight: '800', color: c.ink },
  additionalCard: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    padding: 10,
    gap: 8,
    ...neu.raised,
  },
  additionalCardDraft: { borderStyle: 'dashed' },
  additionalMeta: { fontSize: 11, color: c.inkSoft },
  additionalMetaDraft: { fontSize: 11, color: c.inkSoft, fontStyle: 'italic' },
  additionalInput: {
    minHeight: 80,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: c.ink,
    ...neu.insetDeep,
  },
  additionalSaveButton: {
    alignSelf: 'flex-end',
    backgroundColor: c.primary,
    borderRadius: radii.pill,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  additionalSaveText: { fontSize: 12, fontWeight: '800', color: c.white },

  // Auto-schedule verification bell + modals.
  bellButton: { padding: 4 },
  bellIcon: { fontSize: 22 },
  bellBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: c.danger,
    borderRadius: radii.pill,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  bellBadgeText: { color: c.white, fontSize: 9, fontWeight: '900' },

  backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },

  sheet: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    maxHeight: '85%',
    paddingTop: 14,
    ...neu.raisedLg,
  },
  sheetTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 6,
    gap: 8,
  },
  sheetTitle: { fontSize: 16, fontWeight: '800', color: c.ink, flexShrink: 1, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  sheetEmptyPadded: { paddingHorizontal: 16, paddingBottom: 16 },
  viewToggleRow: {
    flexDirection: 'row',
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    padding: 2,
    gap: 2,
    ...neu.inset,
  },
  viewToggleBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: radii.pill },
  viewToggleBtnActive: { backgroundColor: c.lime },
  viewToggleText: { fontSize: 11, fontWeight: '800', color: c.inkSoft },
  viewToggleTextActive: { color: c.blueText },
  sheetScroll: { paddingHorizontal: 16 },
  sheetScrollContent: { paddingBottom: 16 },
  sheetFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: c.glassStroke,
  },
  sheetDoneButton: {
    backgroundColor: c.primary,
    borderRadius: radii.pill,
    paddingHorizontal: 18,
    paddingVertical: 8,
  },
  sheetDoneButtonText: { fontSize: 13, fontWeight: '800', color: c.white },

  keepAllBtn: {
    backgroundColor: c.glassFill,
    borderRadius: radii.tile,
    paddingVertical: 9,
    alignItems: 'center',
    marginBottom: 4,
    ...neu.raisedSm,
  },
  keepAllBtnText: { fontSize: 13, fontWeight: '800', color: c.ink },

  logRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: c.glassStroke,
    paddingVertical: 10,
    gap: 8,
  },
  logRowText: { flex: 1 },
  logTitle: { fontSize: 14, fontWeight: '700', color: c.danger, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  logMeta: { fontSize: 11, color: c.inkSoft, marginTop: 2 },
  logRowActions: { flexDirection: 'row', gap: 6 },
  logActionBtn: {
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
    ...neu.raisedSm,
  },
  logActionText: { fontSize: 11, fontWeight: '800', color: c.ink },

  // Verification bell — Grid (week-calendar) view.
  gridRoot: { paddingHorizontal: 16, paddingBottom: 16, gap: 8 },
  gridNavRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  gridNavBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radii.pill,
    backgroundColor: c.glassFill,
    ...neu.raisedSm,
  },
  gridNavBtnText: { fontSize: 16, fontWeight: '900', color: c.blueText },
  gridTodayBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: radii.pill,
    backgroundColor: c.glassFill,
    ...neu.raisedSm,
  },
  gridTodayBtnText: { fontSize: 11, fontWeight: '800', color: c.ink },
  gridWeekLabel: { fontSize: 11, fontWeight: '700', color: c.inkSoft, marginLeft: 4 },
  gridColumns: { flexDirection: 'row', gap: 4, height: 260 },
  gridDayCol: {
    flex: 1,
    borderRadius: radii.tile,
    backgroundColor: c.glassFill,
    overflow: 'hidden',
    ...neu.raised,
  },
  gridDayColToday: { ...neu.glowPrimary },
  gridDayHeader: {
    fontSize: 10,
    fontWeight: '800',
    color: c.inkSoft,
    textAlign: 'center',
    paddingVertical: 4,
    backgroundColor: c.limeSoft,
  },
  gridDayHeaderToday: { color: c.ink, fontWeight: '800' },
  gridDayItems: { flex: 1 },
  gridDayItemsContent: { padding: 3, gap: 3 },
  gridDayEmpty: { fontSize: 10, color: c.inkSoft, textAlign: 'center', marginTop: 8 },
  gridItem: {
    backgroundColor: c.danger,
    borderRadius: 4,
    padding: 3,
    gap: 2,
  },
  gridItemText: { fontSize: 9, fontWeight: '700', color: c.white },
  gridItemKeepBtn: {
    alignSelf: 'flex-start',
    backgroundColor: c.card,
    borderRadius: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  gridItemKeepBtnText: { fontSize: 8, fontWeight: '800', color: c.danger },

  modalBox: {
    margin: 20,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.panel,
    padding: 16,
    alignSelf: 'stretch',
    ...neu.raisedLg,
  },
  modalTitle: { fontSize: 15, fontWeight: '800', color: c.ink, marginBottom: 10, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  fieldLabel: { fontSize: 12, fontWeight: '800', color: c.inkSoft, marginBottom: 4 },
  fieldLabelSpaced: { marginTop: 12 },
  modalInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 14,
    color: c.ink,
    ...neu.insetDeep,
  },
  startedReasonInput: { minHeight: 90 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  modalChip: {
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 5,
    ...neu.raisedSm,
  },
  modalChipText: { fontSize: 12, fontWeight: '700', color: c.ink },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  modalBtn: { paddingHorizontal: 14, paddingVertical: 9, borderRadius: radii.tile },
  modalBtnText: { fontSize: 13, fontWeight: '800', color: c.ink },
  modalBtnPrimary: { backgroundColor: c.primary },
  modalBtnPrimaryText: { color: c.white },

  // EPIC section styles
  epicBlock: { gap: 8 },
  epicSectionTitle: { fontSize: 14, fontWeight: '800', color: c.blueText, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: DISPLAY_FONT },
  epicLoadingContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: 16, gap: 8 },
  epicLoadingText: { fontSize: 12, color: c.slate },
  epicErrorText: { fontSize: 13, color: c.danger },
  epicCard: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.panel,
    padding: 14,
    gap: 10,
    ...neu.raised,
  },
  epicCardHeader: { gap: 4 },
  epicEpisodeNumber: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '800',
    color: c.inkSoft,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  epicEpisodeTitle: { fontSize: 16, fontWeight: '800', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  epicAudioSection: { gap: 8 },
  epicPlayButton: {
    backgroundColor: c.primary,
    borderRadius: radii.pill,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  epicPlayButtonActive: { opacity: 0.8 },
  epicPlayButtonText: { fontWeight: '700', color: c.white, fontSize: 13 },
  epicGenerateAudioButton: {
    backgroundColor: c.primary,
    borderRadius: radii.pill,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  epicGenerateAudioButtonDisabled: { opacity: 0.6 },
  epicGenerateAudioButtonText: { fontWeight: '700', color: c.white, fontSize: 13 },
  epicTranscriptContainer: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    padding: 10,
    maxHeight: 200,
    ...neu.inset,
  },
  epicTranscriptText: { fontSize: 12, lineHeight: 18, color: c.ink },
}),
);
